import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { UUID } from '@aline/common';
import {
  IconButton,
  InputAdornment,
  InputBase,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Popover,
} from '@mui/material';
import clsx from 'clsx';
import { Plus, Search } from 'lucide-react';
import { useSnackbar } from 'notistack';
import type { ActivityItemFragment } from 'src/components';
import {
  AlineTooltip,
  ConfirmationDialog,
  ConfirmationDialogVariant,
  useHandleAddCollaborator,
} from 'src/components';
import type {
  WorkflowAssignmentAutocompleteProps,
  WorkflowAssignmentSelection,
} from 'src/components/workflow/WorkflowAssignmentAutocomplete';
import {
  getWorkflowAssignmentOptionIdentity,
  toWorkflowCollaboratorOption,
  WorkflowAssignmentAutocomplete,
  WorkflowAssignmentAutocompleteContext,
} from 'src/components/workflow/WorkflowAssignmentAutocomplete';
import { Apollo } from 'src/gql/generated';
import { useSetTaskAssignees } from 'src/hooks';
import { important } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

import type { AssigneeWritePlan } from './task-assignee-write-plan';
import {
  getPickedAssigneeInput,
  planAddPermission,
  planAddReviewerWithAssignee,
  planAssigneeToggle,
  planUnassignAll,
  toTaskAssigneeInput,
} from './task-assignee-write-plan';
import { AddAssigneeForReviewerDialog } from './task-review/AddAssigneeForReviewerDialog';
import { getHasAssigneeSide } from './task-review/task-review-helpers';

const useStyles = makeStyles()(theme => ({
  addButton: {
    flexShrink: 0,
  },
  addButtonOpen: {
    backgroundColor: theme.appColors.selectedBackground,
  },
  autocompleteContainer: {
    '& .MuiAutocomplete-popper': {
      position: important('static'),
      transform: important('none'),
    },
    borderRadius: 3,
    overflow: 'visible',
    width: 320,
  },
  disablePortal: {
    position: 'static',
  },
  input: {
    borderBottom: `1px solid ${theme.appColors.rule}`,
    boxSizing: 'border-box',
    padding: theme.spacing(1),
  },
  listBox: {
    maxHeight: 240,
    overflow: 'auto',
  },
  listPaper: {
    boxShadow: 'unset',
    margin: 'unset',
    maxHeight: 'none',
  },
  placeholder: {
    color: theme.appColors.secondaryType,
  },
  popper: {
    zIndex: theme.zIndex.modal - 1,
  },
  roleDetail: {
    color: theme.appColors.iconsType,
    fontSize: 12,
  },
  roleItem: {
    maxWidth: 280,
    whiteSpace: 'normal',
  },
  roleLabel: {
    fontSize: 14,
    fontWeight: 600,
  },
}));

type TaskPermission = Apollo.TaskAssignmentFieldsFragment['permission'];
type TaskPermissions = TaskPermission[];
type TaskAssignmentSnapshot = Pick<
  NonNullable<NonNullable<Apollo.GetTaskWithCommentsQueryResult['data']>['task']>,
  | '__typename'
  | 'alineAiRunMessageId'
  | 'assignments'
  | 'counterpartyName'
  | 'hasExternalAssignees'
  | 'id'
  | 'isDelegatedToAi'
  | 'startedAt'
  | 'status'
  | 'viewerActionRecipients'
>;
type TaskAssignment = TaskAssignmentSnapshot['assignments'][number];

interface TaskAssigneePickerProps {
  isReadOnly: boolean;
  task: TaskAssignmentSnapshot;
  workflowId: UUID;
  editDisabledReason?: string | null;
  onAlineAiActivityStarted?: (activityItem: ActivityItemFragment) => void;
}

/** A plus button that opens the picker that adds and removes people or Aline AI; nothing when read-only. */
export const TaskAssigneePicker = React.memo(function TaskAssigneePicker({
  editDisabledReason,
  isReadOnly,
  onAlineAiActivityStarted,
  task,
  workflowId,
}: TaskAssigneePickerProps) {
  const taskId = task.id;
  const permissions = useMemo(
    () => task.assignments.map(assignment => assignment.permission),
    [task.assignments]
  );
  const isDelegatedToAi = task.isDelegatedToAi;
  const { enqueueSnackbar } = useSnackbar();
  const { classes } = useStyles();
  const { setTaskAssignees } = useSetTaskAssignees();
  const [setTaskDelegatedToAi] = Apollo.useSetTaskDelegatedToAiMutation();
  const [startTaskAiExecution] = Apollo.useStartTaskAiExecutionMutation();
  const { handleAddCollaborator } = useHandleAddCollaborator(workflowId);
  const [showRunAlineAiDialog, setShowRunAlineAiDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  // A reviewer picked while nobody does the work waits here until an assignee is chosen.
  const [pendingReviewer, setPendingReviewer] = useState<WorkflowAssignmentSelection | null>(null);
  // The role new picks join in; the "+" asks for it before the picker opens.
  const [pickerRole, setPickerRole] = useState<Apollo.TaskAssigneeRole | null>(null);
  const hasAlineAiRun = Boolean(task.alineAiRunMessageId);
  const pickerOpen = Boolean(anchorEl) && Boolean(pickerRole);
  const isReviewerPicker = pickerRole === Apollo.TaskAssigneeRole.Reviewer;
  /**
   * The picker lists the people in the role it adds. Someone already on the task in the other role
   * is left out; their row's "..." menu switches them.
   */
  const { otherRoleOptions, sameRoleOptions } = useMemo(() => {
    const getIsSameRole = (assignment: TaskAssignment) =>
      !pickerRole || assignment.role === pickerRole;
    const toOption = (assignment: TaskAssignment) =>
      toWorkflowCollaboratorOption(assignment.permission);

    return {
      otherRoleOptions: task.assignments
        .filter(assignment => !getIsSameRole(assignment))
        .map(toOption),
      sameRoleOptions: task.assignments.filter(getIsSameRole).map(toOption),
    };
  }, [pickerRole, task.assignments]);

  const handleClosePicker = useCallback(() => {
    setAnchorEl(null);
    setPickerRole(null);
  }, []);

  const setAlineAiDelegation = useCallback(
    async (delegated: boolean) => {
      const { data, error } = await setTaskDelegatedToAi({
        variables: { delegated, taskId },
      });
      // errorPolicy 'all' can resolve with both data and error — only a clean result counts.
      if (error || !data) return false;
      enqueueSnackbar(
        delegated
          ? 'Aline AI is now delegated to this task.'
          : 'Aline AI is no longer delegated to this task.',
        { variant: 'success' }
      );
      const { activityItem } = data.setTaskDelegatedToAi;
      if (delegated && activityItem) onAlineAiActivityStarted?.(activityItem);
      return true;
    },
    [enqueueSnackbar, onAlineAiActivityStarted, setTaskDelegatedToAi, taskId]
  );

  /**
   * The newest assignee list we know of: the last mutation's result while
   * writes are in flight, the rendered props otherwise. The picker stays open
   * for several picks, so the props of the click that started a write are
   * already stale by the time the next click composes its list.
   */
  const latestPermissionsRef = useRef<TaskPermissions>(permissions);
  useEffect(() => {
    latestPermissionsRef.current = permissions;
  }, [permissions]);
  const writeQueueRef = useRef<Promise<boolean>>(Promise.resolve(true));
  /**
   * Options whose write has not landed yet. The picker's checkboxes come from
   * the rendered props, so an add that carries no optimistic list leaves the
   * row looking unpicked; without this, clicking it again would read as "remove
   * the person I just added" and the queued set-list write would drop them.
   */
  const inFlightOptionIdsRef = useRef(new Set<string>());

  /**
   * Queue one set-list write. Writes are serialized and each one builds its
   * desired list when it runs rather than when it was clicked, so picking two
   * people in quick succession assigns both instead of the second replacing
   * the first.
   */
  const queueAssigneeWrite = useCallback(
    (
      buildPlan: (current: TaskPermissions) => Promise<AssigneeWritePlan<TaskPermission> | null>
    ) => {
      const run = writeQueueRef.current.then(async () => {
        const plan = await buildPlan(latestPermissionsRef.current);
        if (!plan) return false;

        const { data, error } = await setTaskAssignees({
          input: { assignees: plan.assignees, taskId },
          optimistic: plan.nextPermissions
            ? { ...task, nextPermissions: plan.nextPermissions }
            : undefined,
          workflowId,
        });
        // The response is authoritative for whatever the next queued write composes on.
        const written = data?.setTaskAssignees.task.assignments.map(
          assignment => assignment.permission
        );
        if (written) latestPermissionsRef.current = written;
        return !error && Boolean(data);
      });
      writeQueueRef.current = run.catch(() => false);
      return run;
    },
    [setTaskAssignees, task, taskId, workflowId]
  );

  const handleAssigneeChange = useCallback(
    async (
      _e: React.ChangeEvent<unknown>,
      option: Parameters<WorkflowAssignmentAutocompleteProps['onChange']>[1]
    ): Promise<boolean> => {
      if (!option) return queueAssigneeWrite(async current => planUnassignAll(current));

      const optionId = getWorkflowAssignmentOptionIdentity(option);
      if (inFlightOptionIdsRef.current.has(optionId)) return false;

      const isFirstReviewer =
        pickerRole === Apollo.TaskAssigneeRole.Reviewer &&
        !getHasAssigneeSide(task) &&
        !sameRoleOptions.some(
          selected => getWorkflowAssignmentOptionIdentity(selected) === optionId
        );
      if (isFirstReviewer) {
        setPendingReviewer(option);
        handleClosePicker();
        return false;
      }

      if (option.__typename === 'AlineAi') {
        const delegated = !isDelegatedToAi;
        const success = await setAlineAiDelegation(delegated);
        if (success && delegated && hasAlineAiRun) {
          setShowRunAlineAiDialog(true);
          handleClosePicker();
        }

        return success;
      }

      inFlightOptionIdsRef.current.add(optionId);
      try {
        const role = pickerRole ?? undefined;
        return await queueAssigneeWrite(async current => {
          const decision = planAssigneeToggle({ current, option, role });
          if (decision.kind === 'noop') return null;
          if (decision.kind === 'write') return decision.plan;
          // Only an org member or a role reaches needsCollaborator.
          if (option.__typename !== 'User' && option.__typename !== 'UserDefinedRole') return null;

          /**
           * Ensure the user or role is a workflow collaborator first; the
           * assignment needs the permission. A null result means that mutation
           * failed — bail out rather than assigning on top of it.
           */
          const permission = await handleAddCollaborator(option);
          if (!permission) return null;

          return planAddPermission(current, permission, role);
        });
      } finally {
        inFlightOptionIdsRef.current.delete(optionId);
      }
    },
    [
      handleAddCollaborator,
      handleClosePicker,
      hasAlineAiRun,
      isDelegatedToAi,
      pickerRole,
      queueAssigneeWrite,
      sameRoleOptions,
      setAlineAiDelegation,
      task,
    ]
  );

  const handleAddReviewerWithAssignee = useCallback(
    async (assignee: WorkflowAssignmentSelection) => {
      const reviewerInput = pendingReviewer ? getPickedAssigneeInput(pendingReviewer) : null;
      if (!reviewerInput) return false;

      const assigneeInput = getPickedAssigneeInput(assignee);
      if (assigneeInput) {
        return queueAssigneeWrite(async current =>
          planAddReviewerWithAssignee(current, {
            assignee: assigneeInput,
            reviewer: reviewerInput,
          })
        );
      }

      // Aline AI does the work by delegation, which must land before the reviewer can join.
      if (!(await setAlineAiDelegation(true))) return false;
      return queueAssigneeWrite(async current => ({
        assignees: [
          ...current.map(toTaskAssigneeInput),
          { ...reviewerInput, role: Apollo.TaskAssigneeRole.Reviewer },
        ],
      }));
    },
    [pendingReviewer, queueAssigneeWrite, setAlineAiDelegation]
  );

  const handleCloseAddAssigneeDialog = useCallback(() => setPendingReviewer(null), []);

  const handlePickerChange = useCallback<WorkflowAssignmentAutocompleteProps['onChange']>(
    async (event, option) => {
      await handleAssigneeChange(event, option);
    },
    [handleAssigneeChange]
  );

  const handleRunAlineAi = useCallback(async () => {
    const result = await startTaskAiExecution({ variables: { taskId } });
    if (!result.data || result.error) return;

    enqueueSnackbar('Aline AI run started.', { variant: 'success' });
    const { activityItem } = result.data.startTaskAiExecution;
    if (activityItem) onAlineAiActivityStarted?.(activityItem);
  }, [enqueueSnackbar, onAlineAiActivityStarted, startTaskAiExecution, taskId]);

  const handleSkipAlineAiRerun = useCallback(() => {
    setShowRunAlineAiDialog(false);
  }, []);

  return (
    <>
      {!isReadOnly && (
        <>
          <AlineTooltip
            arrow
            placement="top"
            title={editDisabledReason ?? 'Add or remove assignees'}
          >
            <span>
              <IconButton
                aria-haspopup="menu"
                aria-label="Add or remove assignees"
                className={clsx(classes.addButton, Boolean(anchorEl) && classes.addButtonOpen)}
                disabled={Boolean(editDisabledReason)}
                size="small"
                onClick={event => setAnchorEl(event.currentTarget)}
              >
                <Plus size={16} />
              </IconButton>
            </span>
          </AlineTooltip>
          <TaskRoleAddMenu
            anchorEl={pickerRole ? null : anchorEl}
            onClose={handleClosePicker}
            onPick={setPickerRole}
          />
          <Popover
            anchorEl={anchorEl}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            className={classes.popper}
            open={pickerOpen}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            onClose={handleClosePicker}
          >
            <Paper
              className={classes.autocompleteContainer}
              onClick={event => event.stopPropagation()}
            >
              <WorkflowAssignmentAutocomplete
                disableCloseOnSelect
                disablePortal
                open
                showSelectionCheckboxes
                classes={{
                  listbox: classes.listBox,
                  paper: classes.listPaper,
                  popperDisablePortal: classes.disablePortal,
                }}
                context={WorkflowAssignmentAutocompleteContext.WorkflowTask}
                disabled={Boolean(editDisabledReason)}
                excludeAlineAi={isReviewerPicker}
                excludedOptions={otherRoleOptions}
                isDelegatedToAi={isDelegatedToAi}
                placeholder={isReviewerPicker ? 'Search reviewers...' : 'Search assignees...'}
                refetch={pickerOpen}
                renderInput={props => {
                  const { slotProps, ...rest } = props;

                  return (
                    <InputBase
                      ref={slotProps.input.ref}
                      autoFocus
                      className={classes.input}
                      inputProps={slotProps.htmlInput}
                      startAdornment={
                        <InputAdornment position="start">
                          <Search size={14} />
                        </InputAdornment>
                      }
                      {...rest}
                    />
                  );
                }}
                value={null}
                values={sameRoleOptions}
                workflowId={workflowId}
                onChange={handlePickerChange}
              />
            </Paper>
          </Popover>
        </>
      )}
      <ConfirmationDialog
        message="Aline AI has already run for this task. Do you want to run it again?"
        open={showRunAlineAiDialog}
        primaryText="Run Aline AI"
        secondaryText="Skip AI"
        title="Run Aline AI again?"
        variant={ConfirmationDialogVariant.Warn}
        onClickPrimary={handleRunAlineAi}
        onClickSecondary={handleSkipAlineAiRerun}
        onClose={handleSkipAlineAiRerun}
      />
      <AddAssigneeForReviewerDialog
        isTemplate={task.status === Apollo.TaskStatus.Template}
        reviewer={pendingReviewer}
        workflowId={workflowId}
        onAdd={handleAddReviewerWithAssignee}
        onClose={handleCloseAddAssigneeDialog}
      />
    </>
  );
});

const ROLE_MENU_ITEMS = [
  {
    detail: 'Does the work and marks it complete.',
    label: 'Add assignee',
    role: Apollo.TaskAssigneeRole.Assignee,
  },
  {
    detail: 'Checks the work, then approves it or requests changes.',
    label: 'Add reviewer',
    role: Apollo.TaskAssigneeRole.Reviewer,
  },
] as const;

interface TaskRoleAddMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onPick: (role: Apollo.TaskAssigneeRole) => void;
}

/** The "+" menu: whether the next people picked do the work or review it. */
function TaskRoleAddMenu({ anchorEl, onClose, onPick }: TaskRoleAddMenuProps): JSX.Element {
  const { classes } = useStyles();
  return (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      open={Boolean(anchorEl)}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      onClose={onClose}
    >
      {ROLE_MENU_ITEMS.map(item => (
        <MenuItem key={item.role} className={classes.roleItem} onClick={() => onPick(item.role)}>
          <ListItemText
            classes={{ primary: classes.roleLabel, secondary: classes.roleDetail }}
            primary={item.label}
            secondary={item.detail}
          />
        </MenuItem>
      ))}
    </Menu>
  );
}
